<template>
  <div class="input-file">
    <input type="file" ref="fileinput" multiple @change="handleDropzoneDrop">
    <label v-if="label">{{ label }}</label>
    <div class="file" v-for="(file, index) in files" :key="index" :style="aspectRatio ? `aspect-ratio:${aspectRatio}` : null">
      <div class="preview" :data-index="index">
        <img v-if="file.base64" :src="file.url || file.base64" alt=""/>
      </div>
    </div>
    <div v-if="files.length < maxFiles" ref="dropzone" class="dropzone" :class="{ hover: hovering }"><div/><Icon name="upload"/></div>
    <button @click="handleDropzoneClick">Datei auswählen</button>
    <span vif="hint" class="hint" v-html="hint" />
  </div>
</template>

<script>
  import Toast from '@/components/ui/Toast';
  import Icon from '@/components/ui/Icon';

  export default {
    name: 'FileInput',
    components: {
      Icon,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      /**
       * Will be used as the label of the component.
       */
      label: {
        type: String,
        default: null,
      },
      /**
       * Maximum Number of Files. Default is Infinity (unlimited)
       */
      maxFiles: {
        type: Number,
        default: Infinity,
      },
      /**
       * Maximum Filesize in Bytes. Default is Infinity (unlimited)
       */
      maxFilesize: {
        type: Number,
        default: Infinity,
      },
      /**
       * Allowed Filetypes. Regular Expression for example 'jpe?g|png|mp4|mov|pdf'
       * Default is all.
       */
      accept: {
        type: String,
        default: null,
      },
      /**
       * Create Previews for supported filetypes
       */
      previews: {
        type: Boolean,
        default: true,
      },
      value: {
        type: [Array],
        default: () => [],
      },
      aspectRatio: {
        type: String,
        default: null,
      },
      hint: {
        type: String,
        default: null,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      return {
        hovering: false,
        files: [],
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {},
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
      value(val) {
        this.files = val;
      },
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    // filters: {},
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    // validations: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // render(h) { return h(); },
    mounted() {
      this.files = this.value || [];
      const { dropzone } = this.$refs;
      if (dropzone) {
        dropzone.addEventListener('click', this.handleDropzoneClick);
        dropzone.addEventListener('dragover', this.handleDropzoneEvent);
        dropzone.addEventListener('dragleave', this.handleDropzoneEvent);
        dropzone.addEventListener('drop', this.handleDropzoneDrop);
      }
    },
    // beforeUpdate() {},
    // updated() {},
    beforeDestroy() {
      const { dropzone } = this.$refs;
      if (dropzone) {
        dropzone.removeEventListener('click', this.handleDropzoneClick);
        dropzone.removeEventListener('dragover', this.handleDropzoneEvent);
        dropzone.removeEventListener('dragleave', this.handleDropzoneEvent);
        dropzone.removeEventListener('drop', this.handleDropzoneDrop);
      }
    },
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      removeFileByIndex(index) {
        const files = (this.files || []).slice();
        files.splice(index, 1);
        this.$emit('update:value', files);
      },
      async processFile(file) {
        // check for invalid filetypes
        if (this.accept) {
          const regex = new RegExp(`(.|/)(${this.accept})$`, 'i');
          if (!(regex.test(file.name) || regex.test(file.type))) {
            Toast.error(`${file.name} is a unsupported filetype.`);
            return null;
          }
        }
        // check for invalid filesizes
        if (file.size === 0 || file.size > this.maxFilesize) {
          Toast.error(`${file.name} is a unsupported filesize.`);
          return null;
        }

        // create previews, if available
        if (this.previews && (file.type || '').includes('image/')) {
          // eslint-disable-next-line no-param-reassign
          file.base64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(file);
          });
        }
        return file;
      },
      //----------------------------------
      // Event Handlers
      //----------------------------------
      handleDropzoneClick() {
        const { fileinput } = this.$refs;
        fileinput.click();
      },
      handleDropzoneEvent(evt) {
        evt.stopPropagation();
        evt.preventDefault();
        this.hovering = evt.type === 'dragover';
      },
      async handleDropzoneDrop(evt) {
        evt.preventDefault();
        evt.preventDefault();
        this.hovering = false;
        const files = evt.target.files || evt.dataTransfer.files;
        const processedFiles = (this.files || []).slice();
        if (processedFiles.length >= this.maxFiles) {
          processedFiles.pop();
        }
        for (let i = 0, n = files.length; i < n; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          const file = await this.processFile(files[i]);
          if (file) {
            processedFiles.push(file);
          }
        }
        this.$emit('update:value', processedFiles);
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

$default-grid-pixels: 8px;

.input-file {
  input[type="file"] {
    display: none;
  }

  & > label {
    display: block;
    @include font-body-medium;
    color: $color-black-solid-100;
    margin-bottom: 4px;
  }

  & > span.hint {
    display: block;
    margin-top: 4px;
    @include font-body-small;
    color: $color-black-alpha-60;
  }

  .dropzone {
    position: relative;
    display: inline-block;
    min-height: auto;
    border: 1px solid $color-black-alpha-60;
    background: 0 0;
    border-radius: $default-border-radius;
    cursor: pointer;
    padding: 0;
    width: calc(35% - #{$default-grid-pixels});
    box-shadow: inset 0 4px 6px -2px rgba(0, 0, 0, 0);
    will-change: box-shadow, background;
    transition: box-shadow $default-transition, background $default-transition;

    & > div {
      position: relative;
      width: 100%;
      padding-bottom: 100%;
    }

    & > svg {
      position: absolute;
      left: 50%;
      top: 50%;
      width: 28px;
      height: 28px;
      opacity: 0.4;
      transform: translate(-50%, -50%);
      color: $color-black-alpha-40;
      will-change: opacity;
      transition: opacity $default-transition;
    }

    &.hover, &:hover {
      box-shadow: inset 0 4px 6px -2px rgba(0, 0, 0, 0.4);
      background: $color-black-alpha-05;

      & > svg {
        opacity: 0.7;
      }
    }
  }

  & > button {
    display: block;
    height: 42px;
    width: 100%;
    border: 0;
    background-color: $color-black-solid-05;
    @include font-button;
    text-transform: uppercase;
    color: $color-black-alpha-60;
  }

  .file {
    position: relative;
    display: inline-block;
    border: 1px solid $color-black-alpha-40;
    border-radius: var(--default-border-radius);
    overflow: hidden;
    width: 100%;
    aspect-ratio: 3.7326732673;

    .preview {
      position: relative;
      width: 100%;
      height: 100%;
      overflow: hidden;

      > img {
        position: absolute;
        object-fit: cover;
        width: 100%;
        height: 100%;
      }

      > div {
        span {
          position: absolute;
          left: 50%;
          top: calc(50% + #{$default-grid-pixels * 2});
          font-size: 88%;
          width: calc(100% - #{$default-grid-pixels * 2});
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          transform: translateX(-50%);
          height: $default-grid-pixels * 2;
          text-align: center;
          cursor: default;
          user-select: none;
          outline: 0;
          display: block;
        }

        .comp-icon {
          position: absolute;
          width: 30%;
          height: 30%;
          top: calc(50% - #{$default-grid-pixels});
          left: 50%;
          transform: translate(-50%, -50%);
        }
      }
    }

    .actions {
      position: absolute;
      display: flex;
      flex-direction: row-reverse;
      justify-content: center;
      align-items: center;
      padding: 0 $default-grid-pixels;
      bottom: 0;
      left: 0;
      width: 100%;
      height: $default-grid-pixels * 3;
      transform: translateY(#{$default-grid-pixels * 3});
      background: $color-black-alpha-80;
      will-change: transform;
      transition: transform $default-transition;

      .action {
        cursor: pointer;
        width: $default-grid-pixels * 2;
        height: $default-grid-pixels * 2;

        &:hover {
          color: white;
        }
      }
    }

    &:hover {
      .actions {
        transform: translateY(0);
      }
    }
  }

  .input-file-tooltip {
    & > p:first-of-type {
      margin-top: 0;
      padding-top: 0;
    }
    & > p:last-of-type {
      margin-bottom: 0;
      padding-bottom: 0;
    }
  }
}
</style>
